import React, { useEffect } from "react";
import { ProjectId } from "../../../backend/model";
import { UploadFile } from "../../../components/upload-file";
import {
  kRegProjectDetailsActions,
  kRegProjectDetailsSelectors,
} from "../../../store/kRegProjectDetailsSlice";
import { useTranslation } from "react-i18next";
import "./style.scss";
import { Card, CardBody, CardTitle } from "@progress/kendo-react-layout";
import { DrawingHistoryGrid } from "./drawing-history-grid";
import { DrawingUpdateProgress } from "./drawing-diff-grid";
import {
  DrawingHistory,
  DrawingUploadResponse,
  DrawingUpdateProgressResponse,
} from "../../../backend/drawing";
import { AppDispatch } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox } from "@progress/kendo-react-inputs";

interface ColumnDrawingProps {
  id: ProjectId;
}

export const ProjectDrawingPage: React.FunctionComponent<
  ColumnDrawingProps
> = ({ id }) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();
  const dataLoading = useSelector(
    kRegProjectDetailsSelectors.selectByIdRequestPending(id),
  );

  const [hist, setHist] = React.useState<DrawingHistory | undefined>(undefined);
  const [ignoreDrilledColumns, setIgnoreDrilledColumns] =
    React.useState<boolean>(false);
  const [progressResponse, setProgressResponse] = React.useState<
    DrawingUpdateProgressResponse | undefined
  >(undefined);
  const [message, setMessage] = React.useState<string | undefined>(undefined);

  const handleDrawingUploadResponse = (response: DrawingUploadResponse) => {
    if (response.success) {
      setMessage("");
      trackUpdateProgress(response.task_id);
    } else {
      setMessage(response.message);
    }
  };

  const updateMessageTemp = (diff: void) => {};

  const doHistoryUpdate = React.useCallback(() => {
    dispatch(kRegProjectDetailsActions.getDrawingHistory(id)).then((entry) => {
      setHist(entry);
    });
  }, [dispatch, id]);

  const trackUpdateProgress = React.useCallback(
    (taskId: string) => {
      dispatch(
        kRegProjectDetailsActions.getDrawingUpdateProgress(id, taskId),
      ).then((response) => {
        setProgressResponse(response);
        if (response.done) {
          doHistoryUpdate();
          dispatch(kRegProjectDetailsActions.getUpdate(id));
        } else {
          setTimeout(() => trackUpdateProgress(taskId), 1000);
        }
      });
    },
    [dispatch, doHistoryUpdate, id],
  );

  useEffect(() => {
    doHistoryUpdate();
    return () => {};
  }, [dispatch, id, doHistoryUpdate]);

  const drawingUpload = (id: ProjectId, filename: string, file: any) => {
    return kRegProjectDetailsActions.postDrawing(
      id,
      filename,
      file,
      ignoreDrilledColumns,
    );
  };

  const drawingLineUpload = (id: ProjectId, filename: string, file: any) => {
    return kRegProjectDetailsActions.postDrawingObstacles(id, file);
  };

  return (
    <div className="kserv-tab-content">
      <div className="kserv-drawing-upload-page k-card-list">
        <div className="k-card-deck">
          <Card className="kserv-project-card">
            <CardBody>
              <CardTitle className="kserv-card-title">
                {t("project.details.upload")}
              </CardTitle>
              <div className="kserv-card-entry">
                <span className="kserv-upload-title">
                  {t("project.details.drawing")}
                </span>
                <UploadFile<DrawingUploadResponse>
                  id={id}
                  extension={"csv"}
                  endpoint={drawingUpload}
                  message={message}
                  setResponse={handleDrawingUploadResponse}
                >
                  <div className="kserv-drawing-checkbox">
                    <Checkbox
                      label={t("project.details.drawingIgnoreDrilledColumns")}
                      checked={ignoreDrilledColumns}
                      onChange={(e) => {
                        const v = e.value;
                        setIgnoreDrilledColumns(v);
                      }}
                    />
                  </div>
                </UploadFile>
              </div>
              <div className="kserv-card-entry">
                <span className="kserv-upload-title">
                  {t("project.details.obstacles")}
                </span>
                <UploadFile<void>
                  id={id}
                  extension={"csv"}
                  endpoint={drawingLineUpload}
                  message={""}
                  setResponse={updateMessageTemp}
                />
              </div>
            </CardBody>
          </Card>
          <Card className="kserv-project-card">
            <CardBody>
              <CardTitle className="kserv-card-title">
                {t("project.drawing.drawingHistory")}
              </CardTitle>
              <div className="kserv-card-entry">
                <DrawingHistoryGrid
                  history={hist ? hist.history : []}
                  dataLoading={dataLoading ? dataLoading : false}
                />
              </div>
            </CardBody>
          </Card>
        </div>
        {progressResponse && (
          <Card className="kserv-project-card">
            <CardBody>
              <CardTitle className="kserv-card-title">
                {t("project.drawing.diffDrawing")}
              </CardTitle>
              <div className="kserv-card-entry">
                <DrawingUpdateProgress
                  response={progressResponse}
                  dataLoading={dataLoading ? dataLoading : false}
                />
              </div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};
